import {
    ChangeDetectionStrategy,
    Component,
    HostListener,
    Input,
    ViewEncapsulation,
    booleanAttribute,
    computed,
    inject,
    input,
    output,
} from '@angular/core';

import { DsTooltipTrigger, OVERLAY_REF_WITH_DATA_TOKEN } from './tooltip.directives';

export const DS_TOOLTIP_VARIANT_ARRAY = ['neutral', 'utility'] as const;
export type DsTooltipVariant = (typeof DS_TOOLTIP_VARIANT_ARRAY)[number];

@Component({
    selector: 'ds-tooltip-content',
    templateUrl: './tooltip.component.html',
    styleUrls: ['./tooltip.component.scss'],
    standalone: true,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[attr.customTooltip]': 'customTooltip()',
    },
})
export class DsTooltipContent {
    inverse = input(false, { transform: booleanAttribute });
    private tooltipTrigger = inject(DsTooltipTrigger);

    protected overlay = inject(OVERLAY_REF_WITH_DATA_TOKEN);

    variant = input<DsTooltipVariant>('neutral');
    fullWidth = input(false, { transform: booleanAttribute });
    fullWidthMode = input<'fullwidth' | 'fullwidthMargin'>('fullwidthMargin');
    customTooltip = input(false, { transform: booleanAttribute });

    protected containerClasses = `ds-tooltip-container`;

    protected outerContainerClasses = computed(() => {
        const positionClass = `ds-tooltip-${this.overlay.data.position}`;
        const arrowClass = `ds-tooltip-arrow-${this.overlay.data.arrowPosition}`;
        const fullWidthClass = this.fullWidth() ? 'ds-tooltip-full-width' : '';
        const inverse = this.inverse() ? 'ds-tooltip-inverse' : '';
        const fullWidthModeClass = this.fullWidthMode() === 'fullwidthMargin' ? 'ds-tooltip-fullwidth-margin' : '';
        const adjustedArrowClass =
            this.overlay.data.position === 'left' || this.overlay.data.position === 'right' ? `ds-tooltip-arrow-middle` : arrowClass; // Adjust arrow for side positions

        return `ds-tooltip-outer-container ds-tooltip-${this.variant()} ${positionClass} ${adjustedArrowClass} ${fullWidthClass} ${fullWidthModeClass} ${inverse}`;
    });

    readonly closed = output<{ type: string; source: string }>();
    @Input() ariaLabel = 'close icon';

    @HostListener('click', ['$event'])
    onActionClick(event: Event): void {
        const target = event.target as HTMLElement;

        // Look for an element with the custom attribute (e.g. dsTooltipClose)
        const actionClose = target.closest('[dsTooltipClose]');
        if (actionClose) {
            this.closeTooltip(event);
        }
    }

    public closeTooltip(event: Event): void {
        if (this.overlay) {
            event.stopPropagation();
            this.closed.emit({ type: event.type, source: 'close' });
            this.tooltipTrigger.tooltipToggleChange.emit({ isTooltipOpen: false, source: 'close' });
            this.overlay.ref.detach();
        }
    }

    // Handle keydown events for accessibility
    public handleKeydown(event: KeyboardEvent): void {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.closeTooltip(event);
        }
    }
}
